/**
 * Language utilities for URL-based internationalization
 */

export const SUPPORTED_LANGUAGES = {
  en: { code: 'en', name: 'English', path: '' },      // Default language, no prefix
  sv: { code: 'sv', name: 'Svenska', path: '/se' },    // Swedish
  es: { code: 'es', name: 'Español', path: '/es' },    // Spanish
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

/**
 * Extract language code from URL pathname
 */
export function getLanguageFromPath(pathname: string): SupportedLanguage {
  // Remove leading slash and get first segment
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];

  // Check if first segment matches a language path
  if (firstSegment === 'se') return 'sv';
  if (firstSegment === 'es') return 'es';

  // Default to English
  return 'en';
}

/**
 * Get the path prefix for a language
 */
export function getLanguagePath(lang: SupportedLanguage): string {
  return SUPPORTED_LANGUAGES[lang].path;
}

/**
 * Remove language prefix from a pathname
 */
export function removeLanguageFromPath(pathname: string): string {
  const lang = getLanguageFromPath(pathname);
  const langPath = getLanguagePath(lang);

  if (langPath && pathname.startsWith(langPath)) {
    return pathname.slice(langPath.length) || '/';
  }

  return pathname;
}

/**
 * Add language prefix to a pathname
 */
export function addLanguageToPath(pathname: string, lang: SupportedLanguage): string {
  const langPath = getLanguagePath(lang);

  // For English (default), don't add prefix
  if (lang === 'en') {
    return pathname;
  }

  // Ensure pathname starts with /
  const cleanPath = pathname.startsWith('/') ? pathname : `/${pathname}`;

  return `${langPath}${cleanPath}`;
}

/**
 * Get all available languages with their display info
 */
export function getAllLanguages() {
  return Object.entries(SUPPORTED_LANGUAGES).map(([code, info]) => ({
    code: code as SupportedLanguage,
    ...info,
  }));
}

/**
 * Check if a language code is supported
 */
export function isSupportedLanguage(code: string): code is SupportedLanguage {
  return code in SUPPORTED_LANGUAGES;
}